import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, Loader2, Play, RefreshCw, Wrench, XCircle } from 'lucide-react';
import { useTestMcp, useTryTool, useUpdateAiSkill } from '@/api/ai-agents';
import type { AiAgent, AiSkill, McpTestResult } from '@/api/types';
import { Badge, Button, Checkbox, Input, Textarea, useToast } from '@/components/ui';
import { cn } from '@/lib/cn';

type Tool = McpTestResult['tools'][number];
interface Param {
  name: string;
  type: string;
  required: boolean;
  description?: string;
}

/** The tool's arguments from its JSON Schema, for display and the "Try it" template. */
function paramsOf(tool: Tool): Param[] {
  const schema = tool.inputSchema as {
    properties?: Record<string, { type?: string | string[]; description?: string; enum?: unknown[] }>;
    required?: string[];
  };
  return Object.entries(schema.properties ?? {}).map(([name, p]) => ({
    name,
    type: p.enum
      ? p.enum.map(String).join(' | ')
      : Array.isArray(p.type)
        ? p.type.join(' | ')
        : (p.type ?? 'any'),
    required: (schema.required ?? []).includes(name),
    description: p.description,
  }));
}

function templateFor(params: Param[]): string {
  const example: Record<string, unknown> = {};
  for (const p of params)
    example[p.name] = p.type === 'number' || p.type === 'integer' ? 0 : p.type === 'boolean' ? false : '';
  return JSON.stringify(example, null, 2);
}

const pretty = (text: string) => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

/**
 * Inside a skill card: checks the connection, lists every tool the MCP server exposes (with its
 * parameters and whether the AI may use it), and lets an admin try a tool — optionally as a
 * signed-in user — to verify what the AI would get back.
 */
export function SkillTools({ agent, skill }: { agent: AiAgent; skill: AiSkill }) {
  const test = useTestMcp();
  const [result, setResult] = useState<McpTestResult | null>(null);
  const [open, setOpen] = useState(false);

  const check = () =>
    test.mutate(
      { agentId: agent.id, skillId: skill.id },
      { onSuccess: setResult, onError: (e) => setResult({ ok: false, message: e.message, tools: [] }) },
    );
  // Verify every skill when the agent opens, so a broken connection shows straight away.
  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skill.id, skill.url, skill.authValueLast4]);

  const tools = result?.tools ?? [];
  const allowed = (name: string) => skill.enabledTools === null || skill.enabledTools.includes(name);
  const enabledCount = tools.filter((t) => allowed(t.name)).length;

  return (
    <div className="w-full border-t border-slate-100 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        {test.isPending && !result ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking connection…
          </span>
        ) : result?.ok ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Connected · {tools.length} tool
            {tools.length === 1 ? '' : 's'}
            {tools.length > 0 && ` · AI can use ${enabledCount}`}
          </span>
        ) : (
          <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-red-700">
            <XCircle className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{result?.message ?? 'Not checked'}</span>
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" loading={test.isPending} onClick={check} aria-label="Check again">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          {tools.length > 0 && (
            <Button variant="ghost" size="sm" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
              <Wrench className="h-3.5 w-3.5" /> {open ? 'Hide tools' : 'Show tools'}
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
            </Button>
          )}
        </span>
      </div>
      {open && (
        <ul className="mt-2 space-y-2">
          {tools.map((t) => (
            <ToolRow
              key={t.name}
              agent={agent}
              skill={skill}
              tool={t}
              allowed={allowed(t.name)}
              allTools={tools}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ToolRow({
  agent,
  skill,
  tool,
  allowed,
  allTools,
}: {
  agent: AiAgent;
  skill: AiSkill;
  tool: Tool;
  allowed: boolean;
  allTools: Tool[];
}) {
  const toast = useToast();
  const update = useUpdateAiSkill();
  const tryTool = useTryTool();
  const params = paramsOf(tool);
  const [trying, setTrying] = useState(false);
  const [args, setArgs] = useState(() => templateFor(params));
  const [asUserId, setAsUserId] = useState('');
  const [argsError, setArgsError] = useState<string | null>(null);

  const setAllowed = (on: boolean) => {
    const current = skill.enabledTools ?? allTools.map((t) => t.name);
    const next = on ? [...new Set([...current, tool.name])] : current.filter((n) => n !== tool.name);
    update.mutate(
      {
        agentId: agent.id,
        skillId: skill.id,
        // Every tool on → back to "all tools", so new tools on the server are picked up too.
        input: { enabledTools: next.length === allTools.length ? null : next },
      },
      { onError: (e) => toast.error(e.message) },
    );
  };

  const run = () => {
    let parsed: Record<string, unknown>;
    try {
      parsed = args.trim() ? (JSON.parse(args) as Record<string, unknown>) : {};
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) throw new Error();
    } catch {
      setArgsError('Arguments must be a JSON object, e.g. { "orderId": "A1" }');
      return;
    }
    setArgsError(null);
    tryTool.mutate(
      {
        agentId: agent.id,
        skillId: skill.id,
        input: {
          tool: tool.name,
          arguments: parsed,
          ...(asUserId.trim() ? { asUserId: asUserId.trim() } : {}),
        },
      },
      { onError: (e) => toast.error(e.message) },
    );
  };

  const res = tryTool.data;
  return (
    <li
      className={cn(
        'rounded-lg border px-3 py-2.5',
        allowed ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50',
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          label={<span className="sr-only">Let the AI use {tool.name}</span>}
          checked={allowed}
          disabled={update.isPending}
          onChange={(e) => setAllowed(e.target.checked)}
        />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-1.5">
            <code className={cn('text-[13px] font-semibold', allowed ? 'text-slate-900' : 'text-slate-400')}>
              {tool.name}
            </code>
            {!allowed && <Badge>AI can’t use</Badge>}
          </p>
          {tool.description && <p className="mt-0.5 text-xs text-slate-500">{tool.description}</p>}
          <div className="mt-1.5 flex flex-wrap gap-1">
            {params.length === 0 ? (
              <span className="text-[11px] text-slate-400">No parameters</span>
            ) : (
              params.map((p) => (
                <span
                  key={p.name}
                  title={p.description}
                  className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
                >
                  {p.name}
                  {p.required && <span className="text-red-500">*</span>}: {p.type}
                </span>
              ))
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setTrying((t) => !t)}>
          <Play className="h-3.5 w-3.5" /> Try it
        </Button>
      </div>

      {trying && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_200px]">
            <Textarea
              label="Arguments (JSON)"
              rows={Math.min(8, Math.max(3, params.length + 2))}
              className="font-mono text-xs"
              value={args}
              onChange={(e) => setArgs(e.target.value)}
              error={argsError ?? undefined}
            />
            <Input
              label="As user id (optional)"
              placeholder="e.g. 6ace6315…"
              hint="Signed like a verified chat, so account tools answer for this user. Empty = anonymous."
              value={asUserId}
              onChange={(e) => setAsUserId(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" loading={tryTool.isPending} onClick={run}>
              <Play className="h-3.5 w-3.5" /> Run {tool.name}
            </Button>
            {res && (
              <span className={cn('text-xs font-medium', res.ok ? 'text-emerald-700' : 'text-red-700')}>
                {res.ok ? 'Worked' : 'Returned an error'} · {res.ms} ms
                {res.asUserId ? ` · as ${res.asUserId}` : ' · anonymous'}
              </span>
            )}
          </div>
          {res && (
            <pre
              className={cn(
                'max-h-72 overflow-auto rounded-lg p-3 text-xs whitespace-pre-wrap',
                res.ok ? 'bg-slate-900 text-slate-100' : 'bg-red-50 text-red-800',
              )}
            >
              {pretty(res.text) || '(empty result)'}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
